<template>
  <layout
    title="Trusted private authentication for banking"
    :transparent="true"
    class="overflow-x-hidden"
  >
    <highlightSection
      backgroundImage="/storage/images/industries/igaming/player_verification.jpg"
      altImage="Accelerate onboarding"
      title="Player onboarding and bonus abuse prevention for Gaming Operators"
      subtitle="Accelerate onboarding, minimize fraud, and automate KYC/AML checks."
      leftButton="See how it works"
      urlLeftButton="https://6992654.fs1.hubspotusercontent-na1.net/hubfs/6992654/01.%20ONE%20PAGERS/Youverse_Gaming.pdf"
    ></highlightSection>

    <EndToEnd
      :endToEndData="endToEnd"
      title="End-to-end player verification and fraud prevention"
    ></EndToEnd>

    <FeatureHighlightSection
      :features="features"
      title="Unlock seamless and safe gaming experiences"
    />
    <div class="pt-20">
      <cta
        title="Get started"
        text="Are you still struggling to get higher player retention rates?"
        buttonText="Book a Demo"
        buttonLink="/book-a-meeting"
        target="_blank"
      />
    </div>

    <div
      class="mt-20 flex md:flex-row flex-col items-center w-full mx-auto px-6 sm:px-7 md:px-4 lg:px-10 xl:px-16 max-w-screen-sm md:max-w-screen-2xl gap-16"
    >
      <div
        class="flex justify-center flex-col md:max-w-full max-w-md md:text-left text-center"
      >
        <h3 class="text-3xl font-bold">Fully automated player verification</h3>
        <p class="mt-6">
          Lower costs and enhance operational efficiency by automatically
          identifying duplicate accounts and detecting bot activity.
        </p>
      </div>
      <rowCardsSection :elements="elements" :hoverAnimation="false" />
    </div>

    <partners />

    <latestPosts
      title="Read our latest articles and resources"
      :tags="['gaming']"
    />

    <div class="mt-20">
      <cta
        title="Talk to our experts"
        text="Your competitors are already ahead. Are you ready?"
        buttonText="Contact us"
        buttonLink="/company#contactus"
      />
    </div>
  </layout>
</template>

<script>
const layout = () =>
  import(/* webpackChunkName: "layout" */ "../../Components/Layout/layout.vue");

const highlightSection = () =>
  import(
    /* webpackChunkName: "highlightSection" */ "../../Components/Layout/highlightSection.vue"
  );

const EndToEnd = () =>
  import(
    /* webpackChunkName: "ProductsInfoCards" */ "../../Components/Layout/UseCases/EndToEndSection.vue"
  );

const FeatureHighlightSection = () =>
  import(
    /* webpackChunkName: "FeatureHighlightSection" */ "../../Components/Layout/UseCases/FeatureHighlightSection.vue"
  );

const cta = () =>
  import(/* webpackChunkName: "cta" */ "../../Components/Layout/cta.vue");

const rowCardsSection = () =>
  import(
    /* webpackChunkName: "rowCardsSection" */ "../../Components/Layout/rowCardsSection.vue"
  );

const partners = () =>
  import(
    /* webpackChunkName: "partners" */ "../../Components/Layout/HomePage/partners.vue"
  );

const latestPosts = () =>
  import(
    /* webpackChunkName: "latestPosts" */ "../../Components/Layout/latestPosts.vue"
  );

export default {
  props: {
    errors: Object,
  },
  components: {
    layout,
    highlightSection,
    EndToEnd,
    FeatureHighlightSection,
    cta,
    rowCardsSection,
    partners,
    latestPosts,
  },
  data() {
    return {
      endToEnd: [
        {
          title: "Onboarding",
          iconPath: "/storage/images/icons/onboarding.svg",
          description: [
            "ID verification",
            "Document check",
            "Liveness and face match",
          ],
        },
        {
          title: "Compliance",
          iconPath: "/storage/images/icons/compliance.svg",
          description: ["AML", "Self Exclusion"],
        },
        {
          title: "Login",
          iconPath: "/storage/images/icons/login.svg",
          description: [
            "Passwordless login",
            "Multi-factor authentication",
            "Password recovery",
          ],
        },
        {
          title: "Fraud Detection",
          iconPath: "/storage/images/icons/fraud.svg",
          description: [
            "Bot/multiple account detection",
            "Account takeover checks",
            "Payment authorization",
          ],
        },
      ],
      elements: [
        {
          title: `Identity Verification`,
          text: `With YouID, you can integrate identity verification flows into your business processes in just minutes.`,
          seeMoreUrl: `/products/youID`,
          imageSrc: `/storage/images/home/icons/identity_verification.svg`,
          imageAlt: `Identity Verification`,
          imageStyle: `w-14 h-14`,
        },
        {
          title: `User Authentication`,
          text: `With YouAuth, you can implement privacy-first user authentication into your app or website in a matter of minutes.`,
          seeMoreUrl: `/products/youAuth`,
          imageSrc: `/storage/images/home/icons/user_authentication.svg`,
          imageAlt: `User Authentication`,
          imageStyle: `w-14 h-14`,
        },
      ],
      features: [
        {
          title: "Simplify player verification",
          description:
            "Prevent unauthorized account takeover, bots, and bonus abuse with AI-powered liveness detection.",
          image:
            "/storage/images/industries/igaming/age_verification_gaming.png",
        },
        {
          title: "Improve onboarding efficiency",
          description:
            "Automate verification processes like age verification and peps & sanctions checks to streamline player onboarding.",
          image:
            "/storage/images/industries/igaming/player_verification_gaming.png",
        },
        {
          title: "Expand your reach",
          description:
            "Meet global regulatory standards and accommodate a diverse customer base with scalable and interoperable solutions.",
          image: "/storage/images/industries/igaming/regulations_gaming.png",
        },
      ],
    };
  },
};
</script>
